import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { useIsFetching } from "@tanstack/react-query";
import {
	Eventcalendar,
	CalendarNav,
	CalendarPrev,
	CalendarToday,
	CalendarNext,
	Popup,
	formatDate,
	MbscCalendarDayData,
	MbscCalendarEvent,
	MbscCalendarEventData,
	MbscEventClickEvent,
	MbscEventcalendarView,
	MbscPageLoadingEvent,
} from "@mobiscroll/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faCircleCheck,
	faChevronLeft,
	faChevronRight,
	faClock,
	faFileExcel,
	faLocationDot,
	faPlus,
	faTags,
} from "@fortawesome/free-solid-svg-icons";
import {
	CCButton,
	CCSegmented,
	CCSegmentedOption,
	CCSpinner,
	useToast,
} from "@pymblecitizencoders/cc-ui";
import { NewEventPayload, useEventManager } from "../hooks/useEventManager";
import {
	monthBounds,
	monthKeyOf,
	useBrowseEvents,
} from "../hooks/useBrowseEvents";
import { useEventSearch } from "../hooks/useEventSearch";
import { supervisorNames } from "../utils/eventOwner";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import { EventFilters } from "../components/eventManager/EventFilters";
import { EventCardsGrid } from "../components/eventManager/EventCardsGrid";
import {
	DayFocus,
	EventListRows,
	ListSelection,
} from "../components/eventManager/EventListRows";
import { useBulkTag } from "../hooks/useBulkTag";
import { useEventTags } from "../hooks/useEventTags";
import { EventTagField } from "../components/eventManager/EventTagField";
import {
	bumpBufferMinutes,
	formatTimeOfDay,
	toDayKey,
	toLocalIsoString,
} from "../utils/eventDates";
import { hexToRgbTriple } from "../utils/eventColour";
import {
	defaultFilters,
	readFilters,
	readRailCollapsed,
	readShowBumpTimes,
	readView,
	writeFilters,
	writeRailCollapsed,
	writeShowBumpTimes,
	writeView,
} from "../utils/eventFilterStorage";
import { matchesFilters } from "../utils/eventFacets";
import { exportEventsToExcel } from "../utils/eventExport";
import { EventCreateFlow } from "../components/eventManager/EventCreateFlow";
import { CalendarEvent, EventFilterState } from "../types/event";
import {
	RequestDraft,
	toCreatePayload,
	toDetailsPatch,
} from "../utils/eventRequest";
import "../components/eventManager/eventManager.css";

/**
 * The five readings of one fetched range.
 *
 * `list` and `cards` are OURS — they are not Mobiscroll views and the
 * Eventcalendar is not mounted for them (see BROWSE_VIEWS). The other three are
 * keys into VIEWS below.
 */
type ViewName = "list" | "cards" | "month" | "week" | "day";

/** The views drawn by this app rather than by Mobiscroll. */
type BrowseView = "list" | "cards";

const isBrowse = (v: ViewName): v is BrowseView =>
	v === "list" || v === "cards";

/**
 * Characters before a search term is worth sending.
 *
 * One letter matches most of the collection, so it costs a full scan to return
 * something nobody wanted. Two is where a term starts to mean something, and it
 * still catches the short ones people really type - "PE", "Yr".
 */
const MIN_SERVER_SEARCH = 2;

/**
 * Events one bulk tag may touch at once.
 *
 * There is no bulk endpoint, so a selection is that many PUTs — and a busy
 * month in this data holds around 8,000 events, which "select everything shown"
 * would happily hand over. The cap is what stands between a mis-click and eight
 * thousand writes. It bounds the SELECTION rather than the write, so the number
 * on screen is always the number that will be sent.
 */
const BULK_LIMIT = 200;

const VIEWS: Record<Exclude<ViewName, BrowseView>, MbscEventcalendarView> = {
	// `labels` is replaced at runtime by however many fit the rendered row —
	// see MONTH_LABEL_FIT and the effect that measures it. This is only the
	// value used for the first paint, before a row exists to measure.
	month: { calendar: { type: "month", labels: 2, eventDisplay: "fill" } },
	/* A timed grid, not a row of labels.
	   Resizing an event to change its duration only exists in a Mobiscroll
	   `schedule` view — a `calendar` view draws labels, which have no edge to
	   grab. Week was labels, so the only place a duration could be changed was
	   the single-day view, which is a poor place to do it: you cannot see the
	   day either side. As a schedule it shows the hour grid across the week, so
	   an event's start and end can be dragged where the rest of the week is
	   visible. `allDay` keeps date-only events in their own row at the top
	   rather than pinning them to midnight. */
	week: { schedule: { type: "week", allDay: true } },
	day: { schedule: { type: "day", allDay: true } },
};

/**
 * How many events a month cell lists before collapsing the rest into "+N More".
 *
 * Was a fixed 2, which suited a laptop and wasted a large screen. Fixing it
 * higher is no better: 4 measured as overflowing the cell at 900px and 760px
 * viewport heights, where a cell is only ~90px tall.
 *
 * So it is measured off the cell that actually rendered. Deriving it from the
 * viewport would mean restating --em-cal-height, its breakpoint and the toolbar
 * height in JS, and those drift apart the moment any of them changes.
 *
 * The three constants are measured from the rendered grid, not read off the
 * chip CSS. The stylesheet's own numbers (20/22/18) still overflowed, because
 * Mobiscroll adds spacing around each label and its day number is taller than
 * the chip's line box.
 */
const LABEL_PITCH = 22; // one label plus the gap Mobiscroll puts under it
const DAY_NUMBER_HEIGHT = 26;
const MORE_LABEL_HEIGHT = 22;
const MIN_LABELS = 2;
const MAX_LABELS = 8;

const labelsForCell = (cellHeight: number): number => {
	const usable = cellHeight - DAY_NUMBER_HEIGHT - MORE_LABEL_HEIGHT;
	const fit = Math.floor(usable / LABEL_PITCH);
	return Math.min(MAX_LABELS, Math.max(MIN_LABELS, fit));
};

/**
 * Full weekday names across the top, as in pymble-swipe's MonthWeekView.
 *
 * Mobiscroll indexes `dayNamesShort` from Sunday. pymble-swipe passes
 * Monday-first into that Sunday-first slot, which mislabels every column by a
 * day; this is the same intent with the order corrected.
 */
const DAY_NAMES = [
	"Sunday",
	"Monday",
	"Tuesday",
	"Wednesday",
	"Thursday",
	"Friday",
	"Saturday",
];

/**
 * View picker. pymble-swipe's CalendarHeader uses bare initials (D/W/M/A) and
 * this did too, with the full word in a visually-hidden span beside each.
 * cc-ui's CCSegmented takes a plain string label, and its guidance is that the
 * label must carry the meaning — `title` is unavailable on touch and unreliable
 * with a screen reader. The calendar is ten columns wide now, so the words fit.
 *
 * Ordered widest-grain-last, matching the design: the two flat readings, then
 * the three calendar grids. Agenda is gone — it was Mobiscroll's own month
 * agenda, which List supersedes and does better: it groups by day, counts each
 * day, collapses, and carries the chips.
 */
const VIEW_SEGMENTS: CCSegmentedOption[] = [
	{ value: "list", label: "List" },
	{ value: "cards", label: "Cards" },
	{ value: "month", label: "Month" },
	{ value: "week", label: "Week" },
	{ value: "day", label: "Day" },
];

/**
 * Bump in and bump out, drawn as the band outside the event.
 *
 * Mobiscroll's own buffer mechanism, which is what its "event buffer" demos
 * use: an event carrying `bufferBefore` / `bufferAfter` minutes gets a block
 * hung off the matching edge, outside its box and inside its column, so the
 * grid reads as "the hall is held from 6am, the assembly runs 9 to 10". The
 * minutes are worked out in `toCalendarEvent`, from a stored `HH:MM` read
 * against the event's own day; this only draws them.
 *
 * WHY NOT MOBISCROLL'S DEFAULT BAND. Left to itself it fills with
 * `currentColor` at 15% — a grey wash, because the colour it inherits here is
 * the text colour, not the event's. Every other event surface in this app is
 * washed with its own CATEGORY colour and outlined in the solid one, and a
 * band that ignored that would read as a different kind of thing rather than
 * as this event's setup time.
 *
 * HATCHED, not a flat wash. A flat 15% block beside a 10% chip is very nearly
 * the chip, and two adjacent events already look like that. Diagonal stripes
 * say "held, not running" at any height — which matters because a band can be
 * fifteen minutes tall, too short for the label to survive the clip.
 *
 * SCHEDULE VIEWS ONLY. `renderBufferBefore` is a scheduler and timeline
 * renderer; the Month grid draws labels, which have no edge to hang anything
 * off, and a date-only event sits in the all-day row where there is no hour
 * axis. Neither is a gap worth filling — see `docs/bump-times.md`, which
 * settled that the month chips have no room for this.
 */
const bumpBand =
	(which: "in" | "out") =>
	(data: MbscCalendarEventData): React.ReactNode => {
		const original = data.original as CalendarEvent | undefined;
		const rgb = hexToRgbTriple(original?.color);
		const at = which === "in" ? original?.BumpIn : original?.BumpOut;
		if (!at) return null;
		return (
			<div
				className={`em-bump-band em-bump-band--${which}`}
				style={{
					background: `repeating-linear-gradient(135deg, rgba(${rgb}, 0.28) 0 5px, rgba(${rgb}, 0.08) 5px 10px)`,
					borderColor: `rgb(${rgb})`,
					color: `rgb(${rgb})`,
				}}
			>
				<span className="em-bump-band__label">
					{which === "in" ? "Bump in" : "Bump out"} {formatTimeOfDay(at)}
				</span>
			</div>
		);
	};

const renderBumpIn = bumpBand("in");
const renderBumpOut = bumpBand("out");

/**
 * The date the calendar was on when the page was left, for this session.
 *
 * Opening an event unmounts this page, and the breadcrumb back remounts it, so
 * `selectedDate` alone came back as today every time: you left October in
 * Month and returned to September. The view already survives that (readView);
 * this is the other half.
 *
 * Module-level rather than a cookie like the view: it is only meant to carry
 * you back from the event you just opened, not to reopen the calendar on a
 * stale week tomorrow. A reload starts on today again. Not PII: a date.
 *
 * `chosen` mirrors `dateChosen`, so a date that was only ever the default is
 * not promoted to a choice on the way back.
 */
let sessionPlace: { date: string; chosen: boolean } | null = null;

const rememberPlace = (date: string, chosen: boolean) => {
	sessionPlace = { date, chosen };
};

/**
 * Whether `iso` falls on the page Mobiscroll is showing. A month page is
 * judged by its month, not its grid: the grid starts on the Monday before
 * the 1st, and a remembered 29 September would otherwise hold October's
 * page to September on the way back.
 */
const isOnPage = (iso: string, event: MbscPageLoadingEvent): boolean => {
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return false;
	const from = event.month ? new Date(event.month) : new Date(event.firstDay);
	const to = event.month
		? new Date(from.getFullYear(), from.getMonth() + 1, 1)
		: new Date(event.lastDay);
	return d >= from && d < to;
};

/**
 * Calendar / list page. Layout follows pymble-swipe's EventListCalendar —
 * filter rail on the left, calendar with a view switcher on the right — but the
 * calendar is Mobiscroll's Eventcalendar wired to the real `event/admin/`
 * endpoint via useEventManager, matching how v2 loads data (lazily, by the
 * visible date range).
 */
export const EventList: React.FC = () => {
	const navigate = useNavigate();

	// Declared before useEventManager because the ticked sources ARE part of the
	// request now — the API returns only those, rather than everything for the
	// rail to sift afterwards. The ticked TAGS are part of it too, for the same
	// reason and with the opposite default: no tags means no tag filter, whereas
	// no sources means no request at all. Restored from the cookie; see
	// eventFilterStorage.
	const [filters, setFilters] = useState<EventFilterState>(readFilters);

	/* The rail can be folded away to give the calendar the full width. Saved,
	   like the filters themselves — see readRailCollapsed on why it is its own
	   cookie rather than another field on EventFilterState. */
	const [railCollapsed, setRailCollapsed] = useState(readRailCollapsed);

	const toggleRail = useCallback(() => {
		setRailCollapsed((prev) => {
			const next = !prev;
			writeRailCollapsed(next);
			return next;
		});
	}, []);

	const {
		events,
		eventsLoaded,
		getLazyData,
		canEdit,
		createEvent,
		updateEvents,
		eventCreating,
		moveEvent,
		setEvents,
	} = useEventManager(undefined, filters.categories, filters.tags);
	const toast = useToast();

	/**
	 * The view on screen, restored from the last visit.
	 *
	 * Leaving the page is not a decision to change view: opening an event and
	 * coming back used to put everyone in Month, whichever of the five they had
	 * been reading. `readView` validates what it finds, so a stale or edited
	 * cookie falls back to Month rather than indexing the Mobiscroll view map to
	 * undefined.
	 */
	const [view, setView] = useState<ViewName>(() => readView());

	/**
	 * Choosing a view saves it. Written here rather than in an effect on `view`:
	 * this is the only place it changes, and an effect would also fire on the
	 * restore itself, rewriting the cookie with what it had just read.
	 */
	const chooseView = useCallback((next: ViewName) => {
		setView(next);
		writeView(next);
	}, []);

	/**
	 * Whether the calendar shows bump in and bump out at all.
	 *
	 * A DISPLAY choice, not a filter: it hides a band, never an event. That is
	 * why it has its own cookie rather than a field on `filters` — see
	 * readShowBumpTimes — and why the rail keeps it below the "Clear all" row
	 * rather than beside the two switches that do take events away.
	 *
	 * IT GOVERNS THE HOVER CARD AS WELL AS THE BANDS, which only became the
	 * right answer when it moved into the rail. In the header it was a
	 * schedule-view control and stopped at the bands, because the rest of the
	 * setting would have been unreachable from Month — a state nobody could see
	 * to turn off. The rail is drawn in all three calendar views, so the switch
	 * is on screen wherever it acts, and "off" can mean what it says: no bump
	 * times anywhere on the calendar.
	 */
	const [showBumpTimes, setShowBumpTimes] = useState<boolean>(() =>
		readShowBumpTimes(),
	);

	const chooseBumpTimes = useCallback((next: boolean) => {
		setShowBumpTimes(next);
		writeShowBumpTimes(next);
	}, []);

	/**
	 * Measure a rendered day cell and show as many events as fit it.
	 *
	 * Only the month view has rows to measure; the others keep their own label
	 * settings. Changing the count re-renders the cells but not the row height —
	 * rows are equal fractions of the calendar's fixed height — so this settles
	 * on the first pass rather than oscillating.
	 */
	const calendarBodyRef = useRef<HTMLDivElement>(null);
	const [monthLabels, setMonthLabels] = useState(MIN_LABELS);

	useEffect(() => {
		const body = calendarBodyRef.current;
		if (!body || view !== "month") return;

		const measure = () => {
			const cell = body.querySelector(".mbsc-calendar-cell");
			if (!cell) return;
			// clientHeight, not the bounding rect: this is the space the labels
			// actually have to sit in.
			const height = cell.clientHeight;
			if (height > 0) setMonthLabels(labelsForCell(height));
		};

		// Coalesce: the grid mutates heavily while it builds, and one measurement
		// per frame is plenty.
		let frame = 0;
		const schedule = () => {
			if (frame) return;
			frame = requestAnimationFrame(() => {
				frame = 0;
				measure();
			});
		};

		measure();

		/* Watch for the grid being BUILT, not just resized.
		 *
		 * A ResizeObserver on this element alone was the bug: .em-calendar-body
		 * takes its height from the flex parent, so populating it with rows never
		 * changes its size and the observer never fired. The one synchronous
		 * measure on mount ran before Mobiscroll had rendered a row, found
		 * nothing, and the count stayed on the fallback of two for good.
		 *
		 * Settling is safe: re-rendering with a new count mutates the cells,
		 * which schedules another measure, which computes the same number, and
		 * React drops the identical state update.
		 */
		const mutations = new MutationObserver(schedule);
		mutations.observe(body, { childList: true, subtree: true });
		const resizes = new ResizeObserver(schedule);
		resizes.observe(body);
		window.addEventListener("resize", schedule);
		return () => {
			if (frame) cancelAnimationFrame(frame);
			mutations.disconnect();
			resizes.disconnect();
			window.removeEventListener("resize", schedule);
		};
	}, [view]);

	const views = useMemo(
		() => ({
			...VIEWS,
			month: {
				calendar: {
					type: "month" as const,
					labels: monthLabels,
					eventDisplay: "fill" as const,
				},
			},
		}),
		[monthLabels],
	);
	const [selectedDate, setSelectedDate] = useState<string>(
		() => sessionPlace?.date ?? toLocalIsoString(new Date()),
	);

	/**
	 * Whether `selectedDate` is a CHOICE or just the default.
	 *
	 * It starts as today either way, so the value alone cannot say which, and a
	 * view being entered needs to know: today by default, the chosen day if there
	 * is one. Set by picking a day on the calendar and by stepping the browse
	 * months; cleared by Today, which restores the default rather than choosing.
	 */
	const [dateChosen, setDateChosen] = useState(
		() => sessionPlace?.chosen ?? false,
	);

	// Every way the date moves - a day click, Today, the browse months - lands
	// in `selectedDate`, so remembering follows it rather than each setter.
	// Page turns in the calendar views do not select a day; onPageLoading
	// records those.
	useEffect(() => {
		rememberPlace(selectedDate, dateChosen);
	}, [selectedDate, dateChosen]);

	/**
	 * The range the calendar is showing, reported by Mobiscroll.
	 *
	 * A ref, not state. Mobiscroll calls `onPageLoading` from inside its own
	 * render, so a `setState` there updates one component while another is
	 * rendering — React warns about exactly that. Nothing draws this value; it
	 * is only read when someone exports, so there is nothing to re-render for.
	 */
	const pageRange = useRef<{ from: Date | null; to: Date | null }>({
		from: null,
		to: null,
	});
	const [exporting, setExporting] = useState(false);

	/**
	 * The month List and Cards START at, as `YYYY-MM`.
	 *
	 * Derived from `selectedDate` so the two readings stay on whatever the
	 * calendar was last showing — switching Month to List keeps you in August
	 * rather than jumping to today.
	 *
	 * A STRING, not a pair of Dates: `selectedDate` changes on every day click,
	 * and a memo returning fresh Date objects would give the query a new identity
	 * each time and refetch the same month on every click.
	 */
	const browseMonth = useMemo(() => {
		const d = new Date(selectedDate);
		return monthKeyOf(Number.isNaN(d.getTime()) ? new Date() : d);
	}, [selectedDate]);

	/**
	 * Fetch the range the browse views are showing.
	 *
	 * The calendar views get this for free: Mobiscroll reports the page it is
	 * about to draw through `onPageLoading` and that call fetches it. List and
	 * Cards do not mount the Eventcalendar, so without this they would render
	 * whatever the last calendar page happened to leave in the cache — and
	 * nothing at all for someone who switches to List before opening a month.
	 *
	 * `pageRange` is set here too, because Export names its file after the range
	 * on screen and that is now this one.
	 */
	const browsing = isBrowse(view);

	/**
	 * The term the API is asked for, which is not the term in the box.
	 *
	 * TWO GUARDS, both about cost. The box fires per keystroke, which was free
	 * while the filtering happened in memory and is not free now that the term
	 * is an unindexed collection scan measured at 130-280ms; the debounce means
	 * one request for a word rather than one per letter. The floor means a
	 * single character never becomes a request at all — "a" matches most of the
	 * calendar, so it is a scan whose answer nobody wants.
	 *
	 * Below the floor the view stays in browse mode and the old in-memory pass
	 * still runs, so one letter narrows what is on screen exactly as it did.
	 */
	const settledSearch = useDebouncedValue(filters.searchText.trim(), 300);
	const serverSearch =
		settledSearch.length >= MIN_SERVER_SEARCH ? settledSearch : "";

	/**
	 * Searching, as a MODE rather than a filter.
	 *
	 * The browse views page through time, four months at a time. A search pages
	 * through matches, over the whole collection. They cannot both drive the
	 * same list, and while the search was a client-side pass over the month
	 * pager's four months, the pager quietly decided what the search could find:
	 * an event matching the term in any other month was not listed, not counted,
	 * and not hinted at.
	 *
	 * Only the browse views. Month, Week and Day are windows on a date by
	 * definition, so a result set spanning years has nowhere to draw itself
	 * there; the term keeps filtering them in memory as before.
	 */
	const searching = browsing && serverSearch.length > 0;

	/**
	 * The browse views have their own reader, paging a month at a time in both
	 * directions rather than taking one fixed window from the calendar's loader.
	 *
	 * Separate from `useEventManager` on purpose. That hook holds ONE range and
	 * replaces its events whenever the range moves, which is exactly right for a
	 * calendar page and exactly wrong for a list that accumulates as you scroll.
	 * `enabled` keeps it idle while a calendar view is showing, so nothing is
	 * fetched for a list nobody is looking at.
	 */
	/* Destructured rather than kept as one object: the hook returns a fresh
	   object every render, so `browse` as a dependency would make every callback
	   below unstable — which is what React.memo on the two views depends on not
	   happening. The fields themselves are memoised. */
	const {
		events: browseEvents,
		months: browseMonths,
		loading: browseLoading,
		loadingEarlier,
		loadingLater,
		loadEarlier,
		loadLater,
		reset: resetBrowse,
	} = useBrowseEvents(
		browseMonth,
		filters.categories,
		browsing && !searching,
		filters.tags,
	);

	const search = useEventSearch(
		serverSearch,
		filters.categories,
		searching,
		filters.tags,
	);

	/**
	 * Are the events on screen still arriving?
	 *
	 * One flag for three readers, because the panel does not care WHICH one is
	 * driving it — only whether what it is about to draw is the answer. A search
	 * reports its own first page; the browse pager reports its anchor month; the
	 * calendar has `eventsLoaded`.
	 *
	 * NOT the incremental loads. `loadingEarlier` / `loadingLater` and Load more
	 * add to a list that is already readable, and they announce themselves on the
	 * edge that asked for them. Covering the panel for those would hide the thing
	 * the reader is scrolling through in order to say that more of it is coming.
	 */
	const eventsBusy = searching
		? search.loading
		: browsing
			? browseLoading
			: !eventsLoaded;

	/**
	 * Anything in flight, including the loads the overlay deliberately ignores.
	 *
	 * `eventsBusy` is about whether what is on screen is the answer yet; this is
	 * about whether the app is talking to the API at all. They differ for every
	 * incremental fetch — another month at either edge, another page of search
	 * results, a calendar page turning — where the list stays readable and only a
	 * small marker is wanted.
	 *
	 * ASKED OF REACT QUERY rather than assembled from the hooks' own flags. Every
	 * event read on this page is keyed under `events`, so one subscription covers
	 * the browse pager, the search, and `getLazyData`'s `fetchQuery` for a
	 * calendar page. The hand-built version missed that last one entirely — it is
	 * a fetch with no hook flag behind it at all, so turning a month in the Month
	 * view reported nothing anywhere.
	 *
	 * The edges say WHICH way a fetch is going; this says only that one is
	 * happening, which is what a header can honestly report from one corner.
	 */
	const fetching = useIsFetching({ queryKey: ["events"] }) > 0;

	/** The span actually in hand, which is what Export names its file after. */
	const browseRange = useMemo(() => {
		const months = browseMonths.length ? browseMonths : [browseMonth];
		return {
			from: monthBounds(months[0]).from,
			to: monthBounds(months[months.length - 1]).to,
		};
	}, [browseMonths, browseMonth]);

	useEffect(() => {
		if (!browsing) return;
		/* A search has no range to name a file after: its results are whatever
		   matched, wherever they fall. `fileName` already drops the date part
		   when both bounds are null, so the export becomes `Events.xlsx`. */
		pageRange.current = searching
			? { from: null, to: null }
			: { from: browseRange.from, to: browseRange.to };
	}, [browsing, browseRange, searching]);

	/**
	 * Today, in the browse views.
	 *
	 * Two things, not one. Moving `selectedDate` only changes the MONTH, so
	 * pressing Today while already reading this month did nothing visible at all
	 * — the button looked broken. The focus request is what actually goes to
	 * today: the view scrolls to that day, or to the first day after it when
	 * today itself has nothing on.
	 *
	 * The counter is the request's identity, so pressing Today again after
	 * scrolling away asks a second time rather than repeating a value the view
	 * has already acted on. See DayFocus.
	 */
	const [dayFocus, setDayFocus] = useState<DayFocus | null>(null);

	/**
	 * The sequence number for EVERY focus request on this page, from one source.
	 *
	 * Both requests land on the same `focus` prop, and `useDayWindow` remembers
	 * the last seq it acted on so a handled request is not replayed. Two
	 * counters, each starting at 1, therefore collide: search, clear the search,
	 * search again, and the second search asks with seq 1 against a window that
	 * has already handled a seq 1. The request is read as old, the window is
	 * never placed, and the results open at the OLDEST match instead of near
	 * today. It fails silently and only some of the time, which is why the
	 * counter is one ref rather than two pieces of state.
	 */
	const focusSeq = useRef(0);
	const nextFocusSeq = useCallback(() => {
		focusSeq.current += 1;
		return focusSeq.current;
	}, []);

	/**
	 * Where a search opens: the match nearest today, not the oldest one.
	 *
	 * Results come back oldest first, because that is the order a calendar
	 * reads in and the order Load more has to extend. Landing at the top of
	 * that would open "assembly" on an assembly from several years ago, which
	 * is a true answer to a question nobody asked.
	 *
	 * `nearest` rather than the Today button's "first day on or after": a
	 * search's matches can stop months ago or resume months out, and the day
	 * just BEFORE today is often the closest thing to an answer. Earlier and
	 * later matches are both one scroll away either way.
	 *
	 * A fresh `seq` per term, so editing the search re-places the window rather
	 * than leaving it where the previous term's results had been read to.
	 */
	const [searchFocus, setSearchFocus] = useState<DayFocus | null>(null);

	useEffect(() => {
		/* Not cleared when the search ends. Nothing reads it outside search mode,
		   and clearing it would only throw away the record of what was last
		   asked for. */
		if (!searching) return;
		setSearchFocus({
			day: toDayKey(new Date()),
			seq: nextFocusSeq(),
			nearest: true,
		});
	}, [searching, serverSearch, nextFocusSeq]);

	/* Stable, so the memoised List and Cards do not re-render every time an
	   unrelated piece of this page's state moves. An inline arrow here was a new
	   prop identity on every render, which defeats React.memo entirely. */
	const openEvent = useCallback(
		(id: string) => navigate(`/event/${id}`),
		[navigate],
	);

	/**
	 * Put the browse views on a given day: select it, ask the window to start
	 * there, and fetch its month if paging has thrown that month away.
	 *
	 * The reset is conditional on purpose. Scrolling forward past `maxPages`
	 * evicts months, and nothing else brings one back: `browseMonth` follows
	 * `selectedDate`, so asking for a day in a month already selected leaves the
	 * query key alone and no refetch happens. Resetting IS a refetch, so it runs
	 * only when the month is genuinely missing — landing on a day that is already
	 * loaded stays instant. The length check matters too: before the first page
	 * arrives `months` is empty, and resetting there would restart the fetch that
	 * is already in flight.
	 */
	const focusDay = useCallback(
		(date: Date) => {
			setSelectedDate(toLocalIsoString(date));
			setDayFocus({ day: toDayKey(date), seq: nextFocusSeq() });
			const month = monthKeyOf(date);
			if (browseMonths.length > 0 && !browseMonths.includes(month)) {
				resetBrowse();
			}
		},
		[browseMonths, resetBrowse, nextFocusSeq],
	);

	/**
	 * Today, in a result list.
	 *
	 * Not `goToToday`. That one moves the MONTH PAGER: it clears `dateChosen`,
	 * sets `selectedDate`, and refetches a month if paging has thrown it away —
	 * all of which is about a reader that is not on screen during a search, and
	 * the last of which would restart a query for a range nobody asked for.
	 *
	 * A search is already holding every match it has fetched, so this is purely
	 * a placement: ask the day window for the match nearest today, the same
	 * request the results were opened with. A new `seq` is what makes it a new
	 * request rather than a repeat of one already handled.
	 */
	const goToTodayInSearch = useCallback(() => {
		setSearchFocus({
			day: toDayKey(new Date()),
			seq: nextFocusSeq(),
			nearest: true,
		});
	}, [nextFocusSeq]);

	const goToToday = useCallback(() => {
		// Today is the DEFAULT, not a choice — so a later view switch defaults to
		// today again rather than treating this press as a date having been picked.
		setDateChosen(false);
		focusDay(new Date());
	}, [focusDay]);

	/**
	 * Entering a view starts it at today, or at the date the person picked.
	 *
	 * Each browse view keeps its own window, so without this, switching List to
	 * Cards dropped you at the start of the oldest month in hand — which after any
	 * scrolling is nowhere in particular. Today is the sensible default, and a
	 * date chosen on the calendar is the sensible exception: pick 14 August in the
	 * month view, switch to List, and you are on 14 August.
	 *
	 * Only the browse views are placed here. The calendar views position
	 * themselves from `selectedDate`, which already holds today until someone
	 * chooses otherwise.
	 */
	const lastView = useRef<ViewName | null>(null);
	useEffect(() => {
		const previous = lastView.current;
		lastView.current = view;
		if (previous === view || !isBrowse(view)) return;

		const chosen = new Date(selectedDate);
		focusDay(
			dateChosen && !Number.isNaN(chosen.getTime()) ? chosen : new Date(),
		);
	}, [view, dateChosen, selectedDate, focusDay]);

	/** Step the browse views a month either way. */
	const shiftBrowseMonth = useCallback((by: number) => {
		// Drop the anchor: a new month is read from its start, not from the day
		// Today happened to leave the last one on.
		setDayFocus(null);
		// Stepping to a month is choosing it, so switching view stays there
		// rather than snapping back to today.
		setDateChosen(true);
		setSelectedDate((prev) => {
			const d = new Date(prev);
			const base = Number.isNaN(d.getTime()) ? new Date() : d;
			return toLocalIsoString(
				new Date(base.getFullYear(), base.getMonth() + by, 1),
			);
		});
	}, []);

	const [tooltip, setTooltip] = useState<{
		open: boolean;
		anchor: HTMLElement | EventTarget | null;
		color: string;
		title: string;
		description: string;
		location: string;
		time: string;
		/**
		 * Bump in and bump out, already formatted, or "" for an event with
		 * neither.
		 *
		 * HERE AS WELL AS THE BAND, because the band is a schedule-view thing
		 * and Month is the view most people are looking at. It is also the only
		 * place the pair is readable when the band is fifteen minutes tall and
		 * its own label has been clipped away.
		 *
		 * One row, not two: an event with only one of them says so ("Bump in
		 * 6:00 AM") rather than spending a line on "Not set" for a field most
		 * events will never carry.
		 */
		bump: string;
		/** The event's banner, or "" for one without. */
		image: string;
		/**
		 * Who is running it, or "" where nobody is recorded.
		 *
		 * Read off the calendar payload like every other field here, which it
		 * has not always been: this used to be the one row that needed its own
		 * request, a debounce to pay for it and an id held here to match the
		 * answer back to the card. `supervisors=1` on the list retired all
		 * three. See `useEventManager`'s `listQueryString`.
		 */
		owner: string;
		/** Supervisors beyond the first, for the "+N more" tail. 0 or more. */
		otherOwners: number;
	}>({
		open: false,
		anchor: null,
		color: "#002f67",
		title: "",
		description: "",
		location: "",
		time: "",
		bump: "",
		image: "",
		owner: "",
		otherOwners: 0,
	});

	const onFilterChange = useCallback(
		<K extends keyof EventFilterState>(key: K, value: EventFilterState[K]) => {
			setFilters((prev) => {
				const next = { ...prev, [key]: value };
				// Written here rather than in an effect on `filters`: this is the
				// only place they change, and an effect would also fire on the
				// restore itself, rewriting the cookie with what it just read.
				writeFilters(next);
				return next;
			});
		},
		[],
	);

	/* Back to what a new person sees, sources included. The rail offers this
	   once, rather than making someone untick a facet at a time to work out
	   which of them emptied the calendar. */
	const onClearAll = useCallback(() => {
		const next = defaultFilters();
		writeFilters(next);
		setFilters(next);
	}, []);

	// Text and location filtering stay client-side, over the range the calendar
	// already fetched. CATEGORY FILTERING DOES NOT: the API now takes the ticked
	// sources and returns only those, so re-checking here would be redundant —
	// and wrong, because it would compare a source id against the `Category`
	// label, which differ for LISS until the sync work lands.
	const fetchedEvents = useMemo<CalendarEvent[]>(
		() => (Array.isArray(events) ? events : []),
		[events],
	);

	/* Whichever reader is driving the view on screen. The rail's counts and the
	   export both follow it, so a facet count in List mode describes the months
	   the list has loaded rather than the month the calendar last drew — and in
	   search mode describes the matches, which is the whole collection's answer
	   rather than one window's. */
	const activeEvents = searching
		? search.events
		: browsing
			? browseEvents
			: fetchedEvents;

	/* `searchApplied` is not an optimisation. The API matches the term against
	   the title OR the short description; this pass only ever looked at the
	   title, so left running over server-side results it would hide every
	   description match after paying to fetch it. See FacetOptions. */
	const facetOpts = useMemo(() => ({ searchApplied: searching }), [searching]);

	const visibleEvents = useMemo(
		() =>
			activeEvents.filter((evt) =>
				matchesFilters(evt, filters, undefined, facetOpts),
			),
		[activeEvents, filters, facetOpts],
	);

	/**
	 * What the Eventcalendar is handed, as against what everything else reads.
	 *
	 * ONE DIFFERENCE: the bump bands, dropped when the switch is off. Mobiscroll
	 * draws a band because an event carries `bufferBefore` / `bufferAfter`
	 * minutes, so the way to not draw one is to not send them — withholding the
	 * renderer only falls back to Mobiscroll's own grey default.
	 *
	 * It copies only the rows that HAVE a band, so an ordinary week rebuilds the
	 * array and hands back every object it was given. That matters more than it
	 * looks: Mobiscroll re-renders an event whose identity changed, and a blanket
	 * `map` would redraw the whole grid on every filter keystroke.
	 *
	 * `visibleEvents` itself is untouched, which is what keeps the export, the
	 * selection count and the List and Cards views out of this: turning a band
	 * off is not a claim about which events exist.
	 */
	const calendarEvents = useMemo(
		() =>
			showBumpTimes
				? visibleEvents
				: visibleEvents.map((evt) =>
						evt.bufferBefore || evt.bufferAfter
							? { ...evt, bufferBefore: undefined, bufferAfter: undefined }
							: evt,
					),
		[visibleEvents, showBumpTimes],
	);

	/* ---------------------------------------------------------------------
	   Bulk tagging

	   Filing many events at once, from any view. The single-event path (the
	   editor's Details tab) is unchanged and remains the only way to REMOVE a
	   tag: this adds.
	   --------------------------------------------------------------------- */

	/**
	 * Whether the page is in "Tag events" mode, and the tags queued to add.
	 *
	 * SELECTION FIRST, TAGS IN THE BAR. This used to arm one tag from the filter
	 * rail and then collect clicks, which put two verbs on every tag row (tick to
	 * filter, icon to arm) and two tag search boxes in one facet. The rail is now
	 * purely a filter, and everything that writes lives in the bar that appears
	 * over the calendar: choose events, find or create the tags, apply.
	 *
	 * The objection that first put arming in the rail — the vocabulary listed in
	 * two places under different rules — does not apply: the bar's picker is
	 * EventTagField, the editor's own field, over the same catalogue and the
	 * same matching rules in `utils/eventTags`.
	 */
	const [assigning, setAssigning] = useState(false);
	const [pendingTags, setPendingTags] = useState<string[]>([]);
	const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
	const { addTags, running: tagging } = useBulkTag();
	const { tags: tagCatalogue } = useEventTags();

	/* Selection is over ids, but the write needs the DOCUMENTS — the whole one,
	   per the note in useBulkTag — so they are looked up out of what is on
	   screen. An id whose event has scrolled out of the retained months, or
	   which a filter has since hidden, is dropped rather than written blind. */
	const selectedEvents = useMemo(
		() =>
			visibleEvents.filter((evt) => selectedIds.has(evt._id ?? String(evt.id))),
		[visibleEvents, selectedIds],
	);

	const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

	/**
	 * Enter or leave "Tag events" mode.
	 *
	 * Both directions drop the selection and the queued tags. Leaving has to,
	 * or the next visit would open with a half-finished job nobody remembers
	 * starting; entering does so for the same reason from the other side.
	 */
	const setTaggingMode = useCallback((on: boolean) => {
		setAssigning(on);
		setPendingTags([]);
		setSelectedIds(new Set());
	}, []);

	/* Set inside the state updaters below, which React may run twice in strict
	   mode — so it is a ref reported by the effect that follows rather than a
	   toast raised from inside the updater, which is a side effect in a place
	   that promises not to have one and fires twice in development. */
	const cappedRef = useRef(false);

	const toggleSelected = useCallback((id: string) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) {
				next.delete(id);
				return next;
			}
			/* The ceiling, enforced wherever a tick can be added. Clicking past it
			   one event at a time is still clicking past it. */
			if (next.size >= BULK_LIMIT) {
				cappedRef.current = true;
				return next;
			}
			next.add(id);
			return next;
		});
	}, []);

	const selection = useMemo<ListSelection>(
		() => ({ selected: selectedIds, onToggle: toggleSelected }),
		[selectedIds, toggleSelected],
	);

	useEffect(() => {
		if (!cappedRef.current) return;
		cappedRef.current = false;
		toast.show({
			message: `At most ${BULK_LIMIT} events can be tagged at once.`,
			severity: "warning",
			duration: 4000,
		});
	}, [selectedIds, toast]);

	const selectAllShown = useCallback(() => {
		setSelectedIds(
			new Set(
				visibleEvents
					.slice(0, BULK_LIMIT)
					.map((evt) => evt._id ?? String(evt.id)),
			),
		);
	}, [visibleEvents]);

	/**
	 * Put the queued tags on every selected event, then say what happened.
	 *
	 * The report distinguishes three outcomes because they mean different
	 * things to the person: written, already carried the tags, and refused. A
	 * bare "done" over a run where nine of forty were refused is the failure
	 * this feature would be remembered for.
	 *
	 * The mode ENDS once the tags have been applied, taking the bar with it: a
	 * mode still live after the job is done, on a page where clicking an event
	 * no longer opens it, is a mode nobody knows they are still in.
	 *
	 * It stays on after a PARTIAL failure, and only then, because the run is
	 * not finished — the events that were refused are still chosen and pressing
	 * again retries exactly those (the rest now carry the tags and are skipped).
	 */
	const onApplyTag = useCallback(async () => {
		/* Every exit from here SAYS something. A silent return is the one
		   behaviour a person cannot tell apart from a broken button. */
		if (selectedEvents.length === 0) {
			toast.show({
				message: "Nothing chosen yet. Click the events you want to tag.",
				severity: "info",
				duration: 4000,
			});
			return;
		}
		if (pendingTags.length === 0) {
			toast.show({
				message: "Choose or create at least one tag to add.",
				severity: "info",
				duration: 4000,
			});
			return;
		}
		const named =
			pendingTags.length === 1
				? `"${pendingTags[0]}"`
				: `${pendingTags.length} tags`;
		const outcome = await addTags(selectedEvents, pendingTags);

		if (outcome.tagged === 0 && outcome.failed.length === 0) {
			toast.show({
				message: `Already tagged ${named}. No change to ${outcome.skipped} ${
					outcome.skipped === 1 ? "event" : "events"
				}.`,
				severity: "info",
				duration: 4000,
			});
			/* Nothing to do IS done, so the mode ends here too. */
			setTaggingMode(false);
			return;
		}

		if (outcome.failed.length > 0) {
			/* Two names at most. The toast is one line and a run of thirty
			   failures would fill the screen with a list nobody can act on; the
			   count carries the rest. */
			const refused = outcome.failed.slice(0, 2).join(", ");
			const more =
				outcome.failed.length > 2
					? ` and ${outcome.failed.length - 2} more`
					: "";
			toast.show({
				message: `Tagged ${outcome.tagged}. Could not tag ${refused}${more}.`,
				severity: "warning",
				duration: 8000,
			});
			return;
		}

		const skipped = outcome.skipped
			? ` ${outcome.skipped} already had ${
					pendingTags.length === 1 ? "it" : "them"
				}.`
			: "";
		toast.show({
			message: `Tagged ${outcome.tagged} ${
				outcome.tagged === 1 ? "event" : "events"
			} with ${named}.${skipped}`,
			severity: "success",
			duration: 4000,
		});
		setTaggingMode(false);
	}, [addTags, pendingTags, selectedEvents, setTaggingMode, toast]);

	/**
	 * What the result list's edges report, or undefined when not searching.
	 *
	 * Memoised: both views are `React.memo`, and a fresh object per render would
	 * defeat that for every unrelated thing this page re-renders for.
	 *
	 * `loaded` is the FETCHED count, not `visibleEvents.length` — it is paired
	 * with `total`, which the API works out before the rail's client-side facets
	 * exist, so counting the two differently would print "showing 12 of 900" for
	 * a set that has 500 in hand.
	 */
	const searchEdges = useMemo(
		() =>
			searching
				? {
						total: search.total,
						loaded: search.events.length,
						loadingMore: search.loadingMore,
						hasMore: search.hasMore,
						onLoadMore: search.loadMore,
					}
				: undefined,
		[
			searching,
			search.total,
			search.events.length,
			search.loadingMore,
			search.hasMore,
			search.loadMore,
		],
	);

	const onPageLoading = useCallback(
		(event: MbscPageLoadingEvent) => {
			// Mobiscroll reports the range it is about to render; fetch it.
			getLazyData(event.firstDay, event.lastDay);
			// Kept for the export, which names its file after the range on
			// screen. Mobiscroll is the only thing that knows what that is —
			// a month page runs from the Monday before the 1st to the Sunday
			// after the last, which neither `selectedDate` nor the view name
			// can tell you.
			pageRange.current = { from: event.firstDay, to: event.lastDay };
			// The arrows turn the page without selecting a day, so the page is
			// what says where someone is. A day they picked that is still on
			// screen is kept; otherwise the page's month (or first day) is
			// remembered, as the default rather than a choice. Module state,
			// not setState - this runs inside Mobiscroll's render.
			const place = sessionPlace;
			if (place && isOnPage(place.date, event)) return;
			rememberPlace(
				toLocalIsoString(new Date(event.month ?? event.firstDay)),
				place?.chosen ?? false,
			);
		},
		[getLazyData],
	);

	const onEventClick = useCallback(
		(args: { event: MbscCalendarEvent }) => {
			const id = (args.event as CalendarEvent)?._id ?? args.event?.id;
			if (!id) return;
			/* While tagging, a click on the grid CHOOSES the event rather than
			   navigating away from the calendar — the same swap the list row and
			   the card make. Navigating would end the run at the first event,
			   which is the opposite of what tagging many at once is for. */
			if (assigning) {
				toggleSelected(String(id));
				return;
			}
			navigate(`/event/${id}`);
		},
		[assigning, navigate, toggleSelected],
	);

	/**
	 * Persist a drag or a resize, and put the event back if it will not stick.
	 *
	 * Mobiscroll has already painted the event in its new place by the time this
	 * runs, and `data` is our own state — so the move is applied locally first
	 * and reverted on failure. Without the revert the calendar would keep
	 * showing a move the server rejected.
	 *
	 * The API validates the pair itself and answers 400 "EndDateTime must be
	 * after StartDateTime". Its wording is about the data, not about any person,
	 * so it is surfaced as-is; anything else gets a generic line, because an
	 * error body can echo the request back and an event document carries
	 * attendee PII.
	 */
	const onEventChanged = useCallback(
		async (args: { event: MbscCalendarEvent }) => {
			const moved = args.event as CalendarEvent;
			const id = moved?._id;
			if (!id) return;

			const start = toLocalIsoString(new Date(moved.start as string));
			const end = toLocalIsoString(new Date(moved.end as string));

			const before = Array.isArray(events) ? (events as CalendarEvent[]) : [];
			const previous = before.find((e) => e._id === id);
			if (!previous) return;

			/* Mobiscroll mutates the event object it was handed, in place, so by
			   now `previous.start` and `.end` ALREADY hold the dropped position —
			   snapshotting the array is not a snapshot of anything. `StartDateTime`
			   and `EndDateTime` are our own keys, which it does not touch, so they
			   are the only surviving record of where the event came from. */
			const wasStart = previous.StartDateTime;
			const wasEnd = previous.EndDateTime;

			/* Mobiscroll reports a move and a resize through the same callback and
			   does not say which happened, so it is read off the result: a move
			   shifts both edges by the same amount and keeps the duration, a
			   resize changes it. Only used to word the confirmation — both persist
			   identically. */
			const spanOf = (from: string, to: string) =>
				new Date(to).getTime() - new Date(from).getTime();
			const resized =
				spanOf(String(wasStart), String(wasEnd)) !== spanOf(start, end);

			/* Optimistic: the calendar is already showing the new slot.

			   THE BUMP BANDS ARE RECOMPUTED, not carried over, and that is the
			   whole reason this is not a two-key spread. `bufferBefore` is a
			   DURATION, so leaving it alone drags the band along with the event
			   and keeps a three-hour bump in three hours before wherever it
			   landed — but `BumpIn` is a TIME OF DAY and a drag does not change
			   it. Six o'clock is still six o'clock; what changes is how far the
			   event now sits from it. Recomputed, a move later in the day widens
			   the band and a move onto another day re-reads it against that day,
			   which is what the document says. */
			setEvents(
				before.map((e) =>
					e._id === id
						? {
								...e,
								start,
								end,
								StartDateTime: start,
								EndDateTime: end,
								bufferBefore: bumpBufferMinutes(start, e.BumpIn, "before"),
								bufferAfter: bumpBufferMinutes(end, e.BumpOut, "after"),
							}
						: e,
				),
			);

			try {
				await moveEvent(
					previous as unknown as Record<string, unknown>,
					start,
					end,
				);
				toast.show({
					message: `"${moved.title}" ${resized ? "resized" : "moved"}.`,
					severity: "success",
					duration: 3000,
				});
			} catch (error) {
				// Rebuild the row from the dates it had, rather than restoring the
				// old array — those objects were mutated by the drag. The bump
				// bands come back with them, by the same recompute: the optimistic
				// update above widened or dropped them against the new slot, and a
				// rejected move has to undo that too.
				setEvents(
					before.map((e) =>
						e._id === id
							? {
									...e,
									start: wasStart,
									end: wasEnd,
									StartDateTime: wasStart,
									EndDateTime: wasEnd,
									bufferBefore: bumpBufferMinutes(wasStart, e.BumpIn, "before"),
									bufferAfter: bumpBufferMinutes(wasEnd, e.BumpOut, "after"),
								}
							: e,
					),
				);
				const status = (error as { response?: { status?: number } })?.response
					?.status;
				const detail = (error as { response?: { data?: { detail?: unknown } } })
					?.response?.data?.detail;
				toast.show({
					message:
						status === 400 && typeof detail === "string"
							? detail
							: `Could not ${resized ? "resize" : "move"} that event. Please try again.`,
					severity: "danger",
					duration: 5000,
				});
			}
		},
		[events, moveEvent, setEvents, toast],
	);

	/** Where a drag across the grid finished. Empty for a click. */
	const [createEnd, setCreateEnd] = useState("");
	/**
	 * True when the grid supplied the date — a double-click or a drag.
	 *
	 * The flow uses it to skip its mode chooser: someone who has just pointed at
	 * a slot does not need to be asked whether they would like help finding one.
	 * The Add event button leaves it false, because there the date really is
	 * still open.
	 */
	const [createFromGrid, setCreateFromGrid] = useState(false);

	/**
	 * Drag across empty grid to create an event over that span.
	 *
	 * Returns false, always. Mobiscroll's own create would insert a provisional
	 * event into `data`, and `data` is derived from the query cache — the
	 * placeholder would vanish on the next render, exactly as noted on
	 * onCellDoubleClick. So the drag is used only to seed the dialog, and the
	 * event exists once the API says it does.
	 *
	 * The dragged range is passed through verbatim rather than rounded: the
	 * person just expressed a duration by hand, and second-guessing it is worse
	 * than honouring it. They can still adjust both ends in the dialog.
	 */
	const onEventCreate = useCallback(
		(args: { event?: MbscCalendarEvent }) => {
			if (!canEdit) return false;

			const start = args.event?.start;
			const end = args.event?.end;
			const from = start ? new Date(start as string) : null;
			const to = end ? new Date(end as string) : null;
			if (!from || Number.isNaN(from.getTime())) return false;

			// Local-naive on both ends — see toLocalIsoString on why
			// toISOString() lands the wrong day either side of midnight.
			setCreateDate(toLocalIsoString(from));
			setCreateEnd(
				to && !Number.isNaN(to.getTime()) ? toLocalIsoString(to) : "",
			);
			setCreateFromGrid(true);
			setCreateOpen(true);
			return false;
		},
		[canEdit],
	);

	const [createOpen, setCreateOpen] = useState(false);
	// The instant the dialog opens against: the clicked cell, or the
	// selected day when it comes from the Add event button.
	const [createDate, setCreateDate] = useState("");

	/**
	 * Create from the request wizard: make the event, write the answers the
	 * create request cannot carry, and hand back the id for the wizard to send
	 * for sign-off. Does NOT navigate; the editor opens once it is published.
	 *
	 * The event exists the moment the first call lands, so a failed second call
	 * does not reject: that would leave the wizard open for a retry that makes a
	 * duplicate. It says what was lost and still returns the id.
	 */
	const requestNewEvent = useCallback(
		async (draft: RequestDraft): Promise<string | null> => {
			const created = await createEvent(toCreatePayload(draft));
			const doc = created?.event ?? created;
			const id: string | null = doc?._id ?? null;
			if (!id) return null;
			try {
				await updateEvents({ ...doc, ...toDetailsPatch(draft) });
			} catch {
				toast.show({
					message:
						"The event was created, but its purpose and location were not saved. Add them in the editor.",
					severity: "danger",
					duration: 7000,
				});
			}
			return id;
		},
		[createEvent, updateEvents, toast],
	);

	/**
	 * Create from the dialog, then go straight to the editor to finish the parts
	 * it deliberately does not ask for. Shared with the double-click path below,
	 * which builds its payload itself and skips the form.
	 */
	const submitNewEvent = useCallback(
		async (payload: NewEventPayload) => {
			try {
				const created = await createEvent(payload);
				setCreateOpen(false);
				const newId = created?._id ?? created?.event?._id;
				const made = created?.event?.Series?.EventIds?.length;
				if (made) {
					// The editor opens on the first; say there are more, or the
					// rest read as if they were never made.
					toast.show({
						message: `${made} events created. You are editing the first.`,
						severity: "success",
						duration: 5000,
					});
				}
				if (newId) {
					navigate(`/event/${newId}`);
				} else {
					toast.show({
						message: "Event added. Refresh if it is not visible.",
						severity: "success",
						duration: 4000,
					});
				}
			} catch {
				// No detail surfaced: API errors can echo the request back, and an
				// event document carries attendee PII. The dialog stays open so
				// nothing typed is lost.
				toast.show({
					message: "Could not add the event. Please try again.",
					severity: "danger",
					duration: 5000,
				});
			}
		},
		[createEvent, navigate, toast],
	);

	/**
	 * Double-click a day to open the create dialog for that day.
	 *
	 * It used to create the event outright and jump to the editor. Opening the
	 * form instead means the two create paths behave the same, and it removes
	 * the hazard of the old one: with no DELETE route on `event/admin/`, an
	 * accidental double-click left an event nobody could remove from this app.
	 * A dialog can simply be cancelled.
	 *
	 * A month or week cell carries no time, so the dialog opens as all-day. A
	 * schedule cell does carry one, so a double-click at 11am opens timed,
	 * 11am to noon — the dialog reads that off the instant passed here.
	 *
	 * `onCellDoubleClick` rather than Mobiscroll's own `clickToCreate` because
	 * `data` is derived from the query cache: Mobiscroll would insert a
	 * provisional event into a list it does not own, which would vanish on the
	 * next render.
	 *
	 * Gated on `canEdit` (the API's `event_admin` flag). The API enforces this
	 * independently — the gate here only avoids offering an action that would
	 * be refused.
	 */
	const openCreateOn = useCallback(
		(date: Date | string | undefined) => {
			if (!canEdit) return;

			const clicked = date ? new Date(date) : new Date();
			if (Number.isNaN(clicked.getTime())) return;

			// Local-naive, not toISOString(): the dialog splits this back into a
			// date and a time for its inputs, and a UTC shift would land the
			// wrong day on either side of midnight.
			setCreateDate(toLocalIsoString(clicked));
			// A point in time states no duration; the dialog applies its own
			// default.
			setCreateEnd("");
			setCreateFromGrid(true);
			setCreateOpen(true);
		},
		[canEdit],
	);

	const onCellDoubleClick = useCallback(
		(args: { date?: Date | string }) => openCreateOn(args.date),
		[openCreateOn],
	);

	/**
	 * A plus in the corner of each month cell, opening the create flow on that
	 * day.
	 *
	 * The two existing ways in are a double-click on the cell and the header's
	 * Add event button. The first is invisible - the comment on the header
	 * button already says an interaction with no affordance is one nobody finds
	 * - and the second opens on whichever day is SELECTED, so creating
	 * something on the 14th means remembering to select the 14th first. This is
	 * the affordance for the day under the pointer, and it seeds the dialog the
	 * same way the double-click does.
	 *
	 * `renderDayContent` rather than `renderDay`: Mobiscroll keeps the cell's
	 * own day number, marks, labels and styling and drops this in beside the
	 * number, which is the only part being added to. It is taken out of flow in
	 * CSS (.em-day-add) so the cell's header does not grow - the label count is
	 * measured off the rendered cell (labelsForCell), and a taller header would
	 * push the last label out of the space it was measured to fit.
	 *
	 * Month only, wired at the call site: `renderDayContent` also renders the
	 * date header of the week and day schedule views, where there is no day
	 * cell to put a corner button in.
	 *
	 * Pointer-only, deliberately - aria-hidden and out of the tab order. A
	 * month page holds six weeks of cells, and giving each one a tab stop would
	 * bury the grid's own day-by-day keyboard navigation. Keyboard users lose
	 * nothing: the header's Add event button is the same dialog on the selected
	 * day, which is the day their focus is already on.
	 */
	const renderDayAdd = useCallback(
		(args: MbscCalendarDayData) => {
			// Gated on canEdit (the API's `event_admin` flag), and off while
			// tagging events: in that mode the grid is a picker, and offering a
			// create button in it invites a click that means something else.
			if (!canEdit || assigning) return null;

			return (
				<button
					type="button"
					className="em-day-add"
					tabIndex={-1}
					aria-hidden="true"
					title={`Add an event on ${formatDate("DDDD D MMMM", args.date)}`}
					onClick={(e) => {
						// The cell underneath selects the day on click and creates
						// on double-click. Neither should happen because someone
						// pressed the button drawn on top of it.
						e.stopPropagation();
						openCreateOn(args.date);
					}}
				>
					<FontAwesomeIcon icon={faPlus} />
				</button>
			);
		},
		[canEdit, assigning, openCreateOn],
	);

	/**
	 * pymble-swipe's `customWeekMonthView`: each event is a rounded chip washed
	 * with 10% of its category colour and outlined in the solid colour, rather
	 * than Mobiscroll's default solid fill. Multi-day events get the same
	 * treatment on a wider bar.
	 */
	const renderEventChip = useCallback(
		(data: MbscCalendarEventData) => {
			const original = data.original as CalendarEvent | undefined;
			const rgb = hexToRgbTriple(original?.color);
			const id = original?._id ?? String(original?.id ?? "");
			const picked = selectedIds.has(id);
			return (
				<div
					className={[
						"em-event-chip",
						data.isMultiDay ? "em-event-chip--multi" : "",
						picked ? "em-event-chip--picked" : "",
					]
						.filter(Boolean)
						.join(" ")}
					style={{
						border: `1px solid rgb(${rgb})`,
						/* A chosen chip fills with its own colour instead of the 10%
						   wash. The tick beside the title carries the meaning; the fill
						   is what makes a dozen chosen events legible across a month
						   grid without reading any of them. */
						background: picked ? `rgba(${rgb}, 0.45)` : `rgba(${rgb}, 0.1)`,
					}}
				>
					{picked && (
						<FontAwesomeIcon
							className="em-event-chip__tick"
							icon={faCircleCheck}
							aria-hidden="true"
						/>
					)}
					<span className="em-event-chip__title">{original?.title}</span>
				</div>
			);
		},
		[selectedIds],
	);

	/**
	 * pymble-swipe's `customDayView`: the day/schedule view gets the same tinted
	 * card, with the banner thumbnail, title, time and location. `renderLabel`
	 * only covers the calendar (month/week) views, so the schedule view needs
	 * its own renderer or it falls back to Mobiscroll's solid block.
	 */
	const renderScheduleEvent = useCallback(
		(data: MbscCalendarEventData) => {
			const original = data.original as CalendarEvent | undefined;
			const rgb = hexToRgbTriple(original?.color);
			const id = original?._id ?? String(original?.id ?? "");
			const picked = selectedIds.has(id);
			const style = {
				border: `1px solid rgb(${rgb})`,
				background: picked ? `rgba(${rgb}, 0.45)` : `rgba(${rgb}, 0.1)`,
			};

			if (data.allDay) {
				return (
					<div
						className={`em-day-event em-day-event--allday${
							picked ? " em-day-event--picked" : ""
						}`}
						style={style}
					>
						{picked && (
							<FontAwesomeIcon icon={faCircleCheck} aria-hidden="true" />
						)}{" "}
						{original?.title}
					</div>
				);
			}

			const image = original?.Banner?.ImageUrl;
			return (
				<div
					className={`em-day-event${picked ? " em-day-event--picked" : ""}`}
					style={style}
				>
					{/* NOTHING when there is no banner. This used to draw a placeholder
				    glyph so the titles stayed aligned down the day, which bought
				    alignment at the price of a 30px square saying "no picture" on
				    every event that has none — and most have none. The title simply
				    starts at the left edge instead, which is where the eye is going
				    anyway. */}
					{image && <img className="em-day-event__image" src={image} alt="" />}
					<div className="em-day-event__details">
						<div className="em-day-event__title">
							{picked && (
								<FontAwesomeIcon icon={faCircleCheck} aria-hidden="true" />
							)}{" "}
							{original?.title}
						</div>
						<div className="em-day-event__meta">
							<FontAwesomeIcon
								icon={faClock}
								style={{ color: `rgb(${rgb})` }}
							/>{" "}
							{data.start} - {data.end}
							{original?.location && (
								<span className="em-day-event__location">
									<FontAwesomeIcon
										icon={faLocationDot}
										style={{ color: `rgb(${rgb})` }}
									/>{" "}
									{original.location}
								</span>
							)}
						</div>
					</div>
				</div>
			);
		},
		[selectedIds],
	);

	/**
	 * Hover tooltip, per pymble-swipe's anchored Popup.
	 *
	 * Only Title, Description, Location and time are read off the event. The
	 * calendar payload spreads the whole event document, and its Attendees array
	 * is not drawn: the list endpoint never builds it, and even where a roll is
	 * in hand it is student and carer PII (see CLAUDE.md). The one thing taken
	 * from it is the OWNER, and that comes from a separate lookup rather than
	 * from this payload — see `useEventOwner` below.
	 *
	 * The concept anchors and colours the tooltip from the hovered event but
	 * fills its body from the create-form's state, so it shows whatever was
	 * last typed rather than what is under the cursor. This reads the hovered
	 * event instead.
	 */
	const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
		undefined,
	);

	useEffect(
		() => () => {
			if (closeTimer.current) clearTimeout(closeTimer.current);
		},
		[],
	);

	const cancelClose = useCallback(() => {
		if (closeTimer.current) {
			clearTimeout(closeTimer.current);
			closeTimer.current = undefined;
		}
	}, []);

	// A short grace period so moving the pointer from the chip onto the tooltip
	// does not dismiss it.
	const scheduleClose = useCallback(() => {
		if (closeTimer.current) return;
		closeTimer.current = setTimeout(() => {
			closeTimer.current = undefined;
			setTooltip((t) => ({ ...t, open: false }));
		}, 200);
	}, []);

	const onEventHoverIn = useCallback(
		(args: MbscEventClickEvent) => {
			cancelClose();
			const evt = args.event as CalendarEvent;
			const time =
				evt.start && evt.end
					? `${formatDate("hh:mm A", new Date(evt.start))} - ${formatDate(
							"hh:mm A",
							new Date(evt.end),
						)}`
					: "";
			const bump = [
				evt.BumpIn && `Bump in ${formatTimeOfDay(evt.BumpIn)}`,
				evt.BumpOut && `Bump out ${formatTimeOfDay(evt.BumpOut)}`,
			]
				.filter(Boolean)
				.join(" · ");
			/* `Supervisors` is absent unless the list was asked for it, and the
			   calendar's fetch is; `supervisorNames` answers [] either way, so a
			   payload without the key simply draws no owner row. */
			const owners = supervisorNames(evt.Supervisors);
			setTooltip({
				open: true,
				anchor: args.domEvent.target,
				color: evt.color || "#002f67",
				title: evt.title || "Untitled event",
				description: evt.Description?.Short || "",
				location: evt.location || "",
				time,
				bump,
				image: evt.Banner?.ImageUrl || "",
				owner: owners[0] ?? "",
				otherOwners: owners.length > 1 ? owners.length - 1 : 0,
			});
		},
		[cancelClose],
	);

	/**
	 * Export what is on screen.
	 *
	 * Reads `visibleEvents`, so the export is the calendar's current page after
	 * the left rail's filters — the list the person is looking at, not the whole
	 * fetch behind it.
	 */
	const onExport = useCallback(async () => {
		setExporting(true);
		try {
			const built = await exportEventsToExcel(visibleEvents, pageRange.current);
			if (!built) {
				toast.show({
					message: "There are no events in this view to export.",
					severity: "warning",
					duration: 3000,
				});
			}
		} catch {
			// No detail surfaced: the failure could quote event data back.
			toast.show({
				message: "Could not build the export. Please try again.",
				severity: "danger",
				duration: 5000,
			});
		} finally {
			setExporting(false);
		}
	}, [visibleEvents, toast]);

	/**
	 * The "Tag events" toggle, shared by both header rows.
	 *
	 * Gated on canEdit: the API enforces `event_admin` on the write regardless,
	 * and this keeps the affordance off everyone else's toolbar rather than
	 * offering a mode that ends in a 403. `aria-pressed` because it holds a mode
	 * rather than running an action.
	 */
	const tagEventsButton = useMemo(
		() =>
			canEdit ? (
				<CCButton
					variant={assigning ? "solid" : "outline"}
					size="sm"
					aria-pressed={assigning}
					disabled={tagging}
					onClick={() => setTaggingMode(!assigning)}
					title={
						assigning
							? "Stop tagging events"
							: "Choose events and add tags to them"
					}
				>
					<FontAwesomeIcon icon={faTags} /> Tag events
				</CCButton>
			) : null,
		[canEdit, assigning, tagging, setTaggingMode],
	);

	/**
	 * pymble-swipe's CalendarHeader row: the month title and the D/W/M view
	 * picker on the left, Today (plus prev/next) on the right.
	 *
	 * It lives in `renderHeader` rather than as a sibling above the grid, which
	 * is where pymble-swipe puts it. CalendarNav, CalendarPrev and friends read
	 * the calendar instance from Mobiscroll's context, so mounted outside the
	 * Eventcalendar they render with nothing to drive — the concept gets away
	 * with it because its calendar is a static mock.
	 */
	const renderHeader = useCallback(
		() => (
			<div className="em-calendar-header">
				<div className="em-cal-header-left">
					<CalendarNav />
					{/* Same marker as the browse header, and the only feedback these
					    views have for a page turn: Mobiscroll asks for the new range
					    through `onPageLoading`, which fetches with no hook state
					    behind it, and the grid goes on drawing the previous month's
					    events until the new ones land. */}
					{fetching && (
						<span className="em-browse-header__busy" title="Loading events">
							<CCSpinner size="inline" decorative />
						</span>
					)}
					<CCSegmented
						label="Calendar view"
						options={VIEW_SEGMENTS}
						value={view}
						onSelect={(v) => chooseView(v as ViewName)}
					/>
				</div>

				<div className="em-cal-header-right">
					<span className="em-cal-nav-buttons">
						<CalendarPrev />
						<CalendarToday />
						<CalendarNext />
					</span>
					{/* Not gated on canEdit: exporting reads what is already on
					    screen, so anyone who can see the calendar can take it
					    away with them. */}
					{tagEventsButton}
					<CCButton
						variant="outline"
						size="sm"
						disabled={exporting}
						onClick={onExport}
						title="Export the events in this view to Excel"
					>
						{exporting ? (
							<CCSpinner size="inline" label="Exporting..." />
						) : (
							<>
								<FontAwesomeIcon icon={faFileExcel} /> Export
							</>
						)}
					</CCButton>

					{/* pymble-swipe has this button in CalendarHeader (commented
					    out in its markup). Double-click on the grid does the same
					    thing, but an interaction with no visible affordance is one
					    nobody finds. */}
					{canEdit && (
						<CCButton
							variant="solid"
							colour="primary"
							size="sm"
							onClick={() => {
								setCreateDate(selectedDate);
								setCreateEnd("");
								// No date chosen yet, so the mode chooser is
								// the right first screen here.
								setCreateFromGrid(false);
								setCreateOpen(true);
							}}
						>
							<FontAwesomeIcon icon={faPlus} /> Add event
						</CCButton>
					)}
				</div>
			</div>
		),
		// Add event seeds the dialog with the selected day, and Export closes
		// over the visible events through onExport. The publishing flag is gone
		// from here with the buttons that needed it. `fetching` is here because
		// the header draws a spinner from it — memoised without it, the marker
		// would appear only when something else happened to change.
		[
			view,
			chooseView,
			canEdit,
			selectedDate,
			onExport,
			exporting,
			fetching,
			tagEventsButton,
		],
	);

	/**
	 * The same header row for List and Cards.
	 *
	 * It cannot reuse `renderHeader`: CalendarNav, CalendarPrev, CalendarToday
	 * and CalendarNext read the calendar instance out of Mobiscroll's context,
	 * and there is no Eventcalendar mounted in these views for them to drive.
	 * So the month label and the three nav buttons are plain ones over
	 * `selectedDate`, which is also what the calendar views set — switching back
	 * to Month lands on the month you were reading.
	 *
	 * Export and Add event are the same controls doing the same thing, kept in
	 * the same place so changing view does not move them under the pointer.
	 */
	/**
	 * What the header calls the loaded span.
	 *
	 * One month reads as "August 2026"; several read as "August to October 2026",
	 * dropping the repeated year. The label has to grow because the span does —
	 * saying "August" over a list that has paged into October would be a lie the
	 * scrollbar cannot correct.
	 */
	const browseSpanLabel = useMemo(() => {
		const opts: Intl.DateTimeFormatOptions = {
			month: "long",
			year: "numeric",
		};
		const from = browseRange.from;
		const to = browseRange.to;
		if (from.getFullYear() === to.getFullYear()) {
			if (from.getMonth() === to.getMonth()) {
				return from.toLocaleDateString("en-AU", opts);
			}
			return `${from.toLocaleDateString("en-AU", { month: "long" })} to ${to.toLocaleDateString("en-AU", opts)}`;
		}
		return `${from.toLocaleDateString("en-AU", opts)} to ${to.toLocaleDateString("en-AU", opts)}`;
	}, [browseRange]);

	const browseHeader = (
		<div className="em-browse-header">
			<div className="em-browse-header__left">
				{/* A search spans whatever it matched, so the month range would be a
				    lie here — it is the pager's span, and the pager is not what is
				    on screen. How many and how far through is on the bottom edge,
				    beside the control that fetches the rest. */}
				<span className="em-browse-header__month">
					{searching ? `Matches for “${serverSearch}”` : browseSpanLabel}
				</span>
				{/* DECORATIVE, so it is not announced. Every fetch this marks is
				    already described in words somewhere a screen reader will reach
				    it — the overlay's status region on a first load, the edge that
				    asked for it on an incremental one. A third live region saying
				    "loading" over the top of those is noise, not access. */}
				{fetching && (
					<span className="em-browse-header__busy" title="Loading events">
						<CCSpinner size="inline" decorative />
					</span>
				)}
				<CCSegmented
					label="Calendar view"
					options={VIEW_SEGMENTS}
					value={view}
					onSelect={(v) => chooseView(v as ViewName)}
				/>
			</div>

			<div className="em-browse-header__right">
				{/* The month steppers are hidden while searching, and Today is not.
				    They step the month PAGER, which is not the reader on screen: a
				    press would move a span the results do not come from and nothing
				    would happen. Today still means something over a result set, so
				    it stays and changes hands — see goToTodayInSearch. */}
				{!searching && (
					<CCButton
						variant="outline"
						size="sm"
						onClick={() => shiftBrowseMonth(-1)}
						title="Previous month"
					>
						<FontAwesomeIcon icon={faChevronLeft} />
						<span className="em-sr-only">Previous month</span>
					</CCButton>
				)}
				<CCButton
					variant="outline"
					size="sm"
					onClick={searching ? goToTodayInSearch : goToToday}
					title={
						searching ? "Jump to the match nearest today" : "Jump to today"
					}
				>
					Today
				</CCButton>
				{!searching && (
					<CCButton
						variant="outline"
						size="sm"
						onClick={() => shiftBrowseMonth(1)}
						title="Next month"
					>
						<FontAwesomeIcon icon={faChevronRight} />
						<span className="em-sr-only">Next month</span>
					</CCButton>
				)}

				{tagEventsButton}
				<CCButton
					variant="outline"
					size="sm"
					disabled={exporting}
					onClick={onExport}
					title="Export the events in this view to Excel"
				>
					{exporting ? (
						<CCSpinner size="inline" label="Exporting..." />
					) : (
						<>
							<FontAwesomeIcon icon={faFileExcel} /> Export
						</>
					)}
				</CCButton>

				{canEdit && (
					<CCButton
						variant="solid"
						colour="primary"
						size="sm"
						onClick={() => {
							setCreateDate(selectedDate);
							setCreateEnd("");
							setCreateFromGrid(false);
							setCreateOpen(true);
						}}
					>
						<FontAwesomeIcon icon={faPlus} /> Add event
					</CCButton>
				)}
			</div>
		</div>
	);

	return (
		<div className="em-page" data-surface="page">
			<div className="em-container">
				<div className="em-grid">
					{/* Two columns of rail, or one when it is folded — the calendar
					    takes the difference either way. */}
					<div className={railCollapsed ? "em-col-lg-1" : "em-col-lg-2"}>
						<EventFilters
							filters={filters}
							/* The fetched set, NOT visibleEvents: a facet's counts have
							   to say what ticking it would give you, which is a
							   question about events the current filters exclude. */
							events={activeEvents}
							searchApplied={searching}
							onFilterChange={onFilterChange}
							onClearAll={onClearAll}
							collapsed={railCollapsed}
							onToggleCollapse={toggleRail}
							/* Only where something is drawn from it. List and Cards
							   show no bump time anywhere, so the switch would be a
							   control with no effect rather than one that is merely
							   off — see the prop's own note. */
							showBumpTimes={isBrowse(view) ? undefined : showBumpTimes}
							onShowBumpTimes={isBrowse(view) ? undefined : chooseBumpTimes}
						/>
					</div>

					<div className={railCollapsed ? "em-col-lg-11" : "em-col-lg-10"}>
						<div className="em-panel em-calendar-wrap" data-surface="card">
							{/* ABOVE the body, so it belongs to no single view. The bar
							    is the same in all five: Month, Week and Day render
							    through Mobiscroll and have no room for a control of
							    ours inside the grid, and putting it in the browse
							    header would have hidden it in exactly those three. */}
							{assigning && (
								<div className="em-assign-bar">
									{/* Both counts, and they are not the same number.
									    `selectedIds` is every event clicked; `selectedEvents`
									    is those the write can still find on screen. They
									    diverge when a filter hides something already chosen,
									    or when a month is paged out from under it — and when
									    they diverge the button writes the SECOND number, so
									    the bar has to show it rather than the flattering
									    one. */}
									<div className="em-assign-bar__row">
										<span className="em-assign-bar__tag">
											<FontAwesomeIcon icon={faTags} aria-hidden="true" />
											<strong>Tag events</strong>
										</span>
										<span
											className="em-assign-bar__hint"
											role="status"
											aria-live="polite"
										>
											{selectedIds.size === 0
												? "Click events to choose them."
												: selectedEvents.length === selectedIds.size
													? `${selectedEvents.length} chosen`
													: `${selectedEvents.length} chosen (${
															selectedIds.size - selectedEvents.length
														} no longer on screen)`}
										</span>
										{isBrowse(view) && (
											<CCButton
												variant="ghost"
												size="sm"
												onClick={selectAllShown}
												disabled={visibleEvents.length === 0}
												title={
													visibleEvents.length > BULK_LIMIT
														? `Choose the first ${BULK_LIMIT} events in this view`
														: "Choose every event in this view"
												}
											>
												{visibleEvents.length > BULK_LIMIT
													? `Select first ${BULK_LIMIT}`
													: `Select all ${visibleEvents.length}`}
											</CCButton>
										)}
										{selectedEvents.length > 0 && (
											<CCButton
												variant="ghost"
												size="sm"
												onClick={clearSelection}
											>
												Clear
											</CCButton>
										)}
									</div>
									<div className="em-assign-bar__row">
										{/* The editor's own field, so finding, creating and
										    deduplicating a tag work exactly as they do on the
										    Details tab. */}
										<div className="em-assign-bar__tags">
											<EventTagField
												label="Tags to add"
												values={pendingTags}
												catalogue={tagCatalogue}
												disabled={tagging}
												onChange={setPendingTags}
											/>
										</div>
										{/* NOT disabled when nothing is chosen. A disabled
										    button is indistinguishable from a broken one:
										    press it, nothing happens, and there is no way to
										    tell whether the feature is dead or you simply have
										    not chosen anything. It says so instead. */}
										<CCButton
											size="sm"
											disabled={tagging}
											onClick={() => void onApplyTag()}
										>
											{tagging ? (
												<>
													<CCSpinner size="inline" decorative /> Tagging...
												</>
											) : (
												`Add to ${selectedEvents.length}`
											)}
										</CCButton>
										<CCButton
											variant="outline"
											size="sm"
											onClick={() => setTaggingMode(false)}
											disabled={tagging}
										>
											Done
										</CCButton>
									</div>
								</div>
							)}
							<div
								className={
									isBrowse(view)
										? "em-calendar-body em-calendar-body--browse"
										: "em-calendar-body"
								}
								ref={calendarBodyRef}
							>
								{/* AN OVERLAY IN THE BROWSE VIEWS, not a replacement.
								    Swapping the whole panel for a spinner took the view
								    switcher, Today and Export with it, so the controls
								    vanished for the length of every load and came back —
								    and on a filter change the panel emptied under the
								    pointer that had just been on a tab.

								    It also covers the window while it is being placed.
								    The list opens at today, which it can only work out
								    once the days are in hand, so an uncovered list is
								    briefly at the wrong day; there is no reason for that
								    to be on screen.

								    The calendar views keep the replacement: Mobiscroll
								    renders its own frame and grid, and a spinner floating
								    over an empty grid says less than one in its place. */}
								{!isBrowse(view) && !eventsLoaded ? (
									<div className="em-loading-block">
										<CCSpinner size="lg" label="Loading events..." />
									</div>
								) : isBrowse(view) ? (
									<>
										{browseHeader}
										{/* The wrapper exists ONLY to be the overlay's
										    containing block. It cannot be `.em-browse-body`
										    itself: that is the scroller, and an absolutely
										    positioned child of a scroller is positioned
										    against the scrolled CONTENT, so the overlay would
										    sit at the top of the list and scroll away with
										    it. */}
										<div className="em-browse-stack">
											{eventsBusy && (
												<div
													className="em-loading-overlay"
													role="status"
													aria-live="polite"
												>
													<CCSpinner size="lg" label="Loading events..." />
												</div>
											)}
											<div className="em-browse-body">
												{view === "list" ? (
													<EventListRows
														events={visibleEvents}
														focus={searching ? searchFocus : dayFocus}
														onOpen={openEvent}
														selection={assigning ? selection : undefined}
														onLoadEarlier={loadEarlier}
														onLoadLater={loadLater}
														loadingEarlier={loadingEarlier}
														loadingLater={loadingLater}
														search={searchEdges}
													/>
												) : (
													<EventCardsGrid
														events={visibleEvents}
														focus={searching ? searchFocus : dayFocus}
														onOpen={openEvent}
														selection={assigning ? selection : undefined}
														onLoadEarlier={loadEarlier}
														onLoadLater={loadLater}
														loadingEarlier={loadingEarlier}
														loadingLater={loadingLater}
														search={searchEdges}
													/>
												)}
											</div>
										</div>
									</>
								) : (
									<Eventcalendar
										height="100%"
										data={calendarEvents}
										view={views[view]}
										selectedDate={selectedDate}
										onSelectedDateChange={(e) => {
											const d = e.date;
											// Local-naive, not toISOString() —
											// see toLocalIsoString on why UTC
											// shifts the rail back a day.
											setSelectedDate(
												d instanceof Date ? toLocalIsoString(d) : String(d),
											);
											// A day picked here is the date a
											// browse view should open on.
											setDateChosen(true);
										}}
										onPageLoading={onPageLoading}
										onEventClick={onEventClick}
										moreEventsText="+{count} More"
										showEventTooltip={false}
										dayNamesShort={DAY_NAMES}
										renderLabel={renderEventChip}
										renderScheduleEvent={renderScheduleEvent}
										// Bump in / bump out. Both are no-ops on
										// an event without the matching time —
										// Mobiscroll only calls them where it
										// has a buffer to draw.
										renderBufferBefore={renderBumpIn}
										renderBufferAfter={renderBumpOut}
										// Month only — see renderDayAdd. In the
										// schedule views this renderer draws the
										// date header, not a day cell.
										renderDayContent={
											view === "month" ? renderDayAdd : undefined
										}
										// Only an event_admin may move an event.
										// The API enforces it too; this stops
										// the drag starting at all rather than
										// letting it snap back on a 403.
										//
										// Off while tagging events: in that mode
										// the grid is a picker, and a click that
										// travels a few pixels would reschedule
										// an event when it meant to choose one.
										dragToMove={canEdit && !assigning}
										// Drag empty grid to create. The
										// handler always returns false — see
										// onEventCreate on why Mobiscroll
										// must not insert the placeholder.
										dragToCreate={canEdit && !assigning}
										onEventCreate={onEventCreate}
										// Drag an edge to change the duration.
										// Fires the same onEventUpdated as a
										// move, so both persist the same way.
										dragToResize={canEdit && !assigning}
										onEventUpdated={(args) => void onEventChanged(args)}
										onCellDoubleClick={
											assigning ? undefined : onCellDoubleClick
										}
										onEventHoverIn={onEventHoverIn}
										onEventHoverOut={scheduleClose}
										renderHeader={renderHeader}
									/>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>

			<Popup
				anchor={tooltip.anchor as HTMLElement}
				cssClass="em-tooltip-popup"
				contentPadding={false}
				display="anchored"
				isOpen={tooltip.open}
				scrollLock={false}
				showOverlay={false}
				touchUi={false}
				width={350}
				onClose={() => setTooltip((t) => ({ ...t, open: false }))}
			>
				<div
					className="em-tooltip"
					onMouseEnter={cancelClose}
					onMouseLeave={scheduleClose}
				>
					{/* The banner, where there is room for it. The grid dropped the
					    30px thumbnail because it was costing the title its width; this
					    is the surface with 350px to spend, and recognising an event by
					    its picture is most of what a banner is for.

					    ABOVE the coloured header rather than inside it, so the header
					    keeps its contrast: the title is painted on the event's own
					    colour, and laying that over a photograph would make it legible
					    on some events and not others. */}
					{tooltip.image && (
						<img
							className="em-tooltip__image"
							src={tooltip.image}
							alt=""
							/* Decorative. It is the same event the title beside it
							   names, so announcing it twice adds nothing. */
						/>
					)}
					<div
						className="em-tooltip__header"
						style={{ backgroundColor: tooltip.color }}
					>
						<span>{tooltip.title}</span>
						{tooltip.time && (
							<span className="em-tooltip__time">{tooltip.time}</span>
						)}
					</div>
					<div className="em-tooltip__body">
						<div className="em-tooltip__row">
							<span className="em-tooltip__label">Location</span>
							<span>{tooltip.location || "Not set"}</span>
						</div>
						{/* Only where there is one, like Bump and unlike Location:
						    an owner is a supervisor on the attendee roll, and most
						    events — every timetable and Clipboard booking among them
						    — have no supervisor recorded at all. A row reading "Not
						    set" on nearly every card would be noise.

						    Named rather than counted, unlike the audience chips. One
						    person is the answer to "who do I ask about this", and the
						    rest are a tail worth acknowledging but not listing in a
						    350px card. */}
						{tooltip.owner && (
							<div className="em-tooltip__row">
								<span className="em-tooltip__label">Owner</span>
								<span>
									{tooltip.owner}
									{tooltip.otherOwners > 0 && (
										<span className="em-tooltip__muted">
											{" "}
											+{tooltip.otherOwners} more
										</span>
									)}
								</span>
							</div>
						)}
						{/* Only where there is one, and only while the rail's
						    Display switch is on. Unlike Location and Description,
						    which every event is expected to fill in, a bump time is
						    the exception — so an absent one is silence rather than
						    a row reading "Not set" on almost every event.

						    Gated here rather than in `onEventHoverIn` so that
						    flicking the switch changes a tooltip that is already
						    open, instead of the next one. */}
						{showBumpTimes && tooltip.bump && (
							<div className="em-tooltip__row">
								<span className="em-tooltip__label">Bump</span>
								<span>{tooltip.bump}</span>
							</div>
						)}
						<div className="em-tooltip__row">
							<span className="em-tooltip__label">Description</span>
							<span>{tooltip.description || "No description"}</span>
						</div>
					</div>
				</div>
			</Popup>

			<EventCreateFlow
				open={createOpen}
				initialDate={createDate || selectedDate}
				initialEnd={createEnd || undefined}
				dateKnown={createFromGrid}
				saving={eventCreating}
				onClose={() => setCreateOpen(false)}
				onCreate={submitNewEvent}
				onRequest={requestNewEvent}
				onOpenEvent={(id) => {
					setCreateOpen(false);
					navigate(`/event/${id}`);
				}}
			/>
		</div>
	);
};
